import { useState, useCallback, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  IconButton,
} from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CloseIcon from '@mui/icons-material/Close'

const PreviewDialog = ({ item, onClose }) => {
  // Keep last non-null item stable through the close animation
  const stableItem = useRef(item)
  if (item) stableItem.current = item
  const displayed = stableItem.current

  const isImage = displayed?.content_type?.startsWith('image/')
  const isPdf = displayed?.content_type === 'application/pdf'

  return (
    <Dialog
      open={Boolean(item)}
      onClose={onClose}
      maxWidth='md'
      scroll='paper'
      fullWidth
      PaperProps={{
        sx: theme => ({
          height: isPdf ? '100%' : 'auto',
          mx: { xs: 1, sm: 3 },
          width: {
            xs: `calc(100% - ${theme.spacing(2 * 1)})`,
            sm: `calc(100% - ${theme.spacing(2 * 3)})`,
          },
        }),
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 1 }}>
        <Typography noWrap sx={{ flex: 1, mx: 1 }}>
          {displayed?.filename}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button href={displayed?.url} target='_blank' rel='noopener noreferrer' component='a' size='small' startIcon={<OpenInNewIcon />} sx={{ color: 'text.secondary' }}>
            New tab
          </Button>
          <IconButton size='small' color='default' onClick={onClose}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {isImage && (
          <img src={displayed.url} alt={displayed.filename} style={{ width: '100%' }} />
        )}
        {isPdf && (
          <iframe src={displayed.url} title={displayed.filename} style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} />
        )}
        {!isImage && !isPdf && (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant='body2'>Preview not available for this file type.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

const AttachmentsList = ({ attachments }) => {
  const [previewItem, setPreviewItem] = useState(null)
  const closePreview = useCallback(() => setPreviewItem(null), [])

  if (!attachments.length) return null

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <AttachFileIcon sx={{ fontSize: 'inherit', color: 'text.secondary' }} />
        <Typography variant='caption' color='text.secondary'>
          Attachments ({attachments.length})
        </Typography>
      </Box>
      <List dense disablePadding>
        {attachments.map((attachment) => (
          <ListItem key={attachment.id} disablePadding>
            <ListItemButton
              sx={{ px: 0.5, py: 0.25 }}
              disableRipple
              onClick={e => { e.currentTarget.blur(); setPreviewItem(attachment) }}
            >
              <ListItemText
                primary={attachment.filename}
                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <PreviewDialog item={previewItem} onClose={closePreview} />
    </>
  )
}

export default AttachmentsList
